import React, { useCallback, useMemo, useReducer } from "react";
import { AuthContext, type AuthUser } from "./AuthContext";

export type AuthProviderProps = {
  children: React.ReactNode;
  initialUser?: AuthUser | null;
  initialLoading?: boolean;
};

type State = {
  isAuthenticated: boolean;
  user: AuthUser | null;
  loading: boolean;
};

type Action =
  | { type: "LOGIN"; payload: AuthUser }
  | { type: "LOGOUT" }
  | { type: "SET_LOADING"; payload: boolean };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "LOGIN":
      return { ...state, isAuthenticated: true, user: action.payload };
    case "LOGOUT":
      return { ...state, isAuthenticated: false, user: null };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    default:
      return state;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  initialUser = null,
  initialLoading = false,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    isAuthenticated: Boolean(initialUser),
    user: initialUser,
    loading: initialLoading,
  });

  const login = useCallback((user: AuthUser) => {
    dispatch({ type: "LOGIN", payload: user });
  }, []);

  const logout = useCallback(() => {
    dispatch({ type: "LOGOUT" });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  }, []);

  const value = useMemo(
    () => ({ ...state, login, logout, setLoading }),
    [state, login, logout, setLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export type { AuthUser } from "./AuthContext";
